// src/features/clients/temp/ClientsMigrationTool.jsx
import React, { useState } from "react";
import { db } from "services/firebaseConfig";
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "components/ui/table";
import { Checkbox } from "components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { useClients } from "../hooks/useClients";
import toast from "react-hot-toast";

const ClientsMigrationTool = () => {
  const [sourceType, setSourceType] = useState("excursions"); // 'excursions' | 'reservations'

  // ✅ 1. Novos estados para controle do filtro
  const [filterType, setFilterType] = useState("month"); // 'month' | 'date'
  const [selectedMonth, setSelectedMonth] = useState(""); // Formato YYYY-MM
  const [selectedDate, setSelectedDate] = useState(""); // Formato YYYY-MM-DD

  const [candidates, setCandidates] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);

  const { addClient } = useClients();

  // 1. Lógica de Busca Atualizada
  const handleSearch = async () => {
    // Validação dependendo do tipo de filtro
    if (filterType === "month" && !selectedMonth)
      return toast.error("Selecione um mês.");
    if (filterType === "date" && !selectedDate)
      return toast.error("Selecione uma data.");

    setIsSearching(true);
    setCandidates([]);
    setSelectedCandidates({});

    try {
      let start, end;

      // ✅ 2. Lógica para definir o intervalo de tempo
      if (filterType === "month") {
        const [year, month] = selectedMonth.split("-").map(Number);
        start = new Date(year, month - 1, 1);
        end = new Date(year, month, 0, 23, 59, 59);
      } else {
        // Filtro por Data Exata
        // Cria a data sem problemas de fuso horário
        const [year, month, day] = selectedDate.split("-").map(Number);
        start = new Date(year, month - 1, day, 0, 0, 0);
        end = new Date(year, month - 1, day, 23, 59, 59);
      }

      // Define coleção e campo de data
      const collectionName = sourceType;
      const dateField =
        sourceType === "excursions" ? "excursionDate" : "checkInDate";

      const q = query(
        collection(db, collectionName),
        where(dateField, ">=", Timestamp.fromDate(start)),
        where(dateField, "<=", Timestamp.fromDate(end))
      );

      const snapshot = await getDocs(q);
      const foundData = [];
      const phoneMap = new Set();

      snapshot.forEach((doc) => {
        const data = doc.data();

        let name = "";
        let phone = "";
        let city = "";

        if (sourceType === "excursions") {
          name = data.clientName;
          phone = data.clientPhone;
          city = data.city || "";
        } else {
          name = data.guestInfo;
          phone = data.phoneInfo;
          city = "";
        }

        const cleanPhone = phone ? phone.replace(/\D/g, "") : "";

        if (cleanPhone && cleanPhone.length >= 8 && !phoneMap.has(cleanPhone)) {
          phoneMap.add(cleanPhone);
          foundData.push({
            id: doc.id,
            cleanId: cleanPhone,
            name,
            phone,
            city,
            source: sourceType,
          });
        }
      });

      setCandidates(foundData);

      const initialSelection = {};
      foundData.forEach((c) => (initialSelection[c.id] = true));
      setSelectedCandidates(initialSelection);

      toast.success(`${foundData.length} registros únicos encontrados.`);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao buscar dados.");
    } finally {
      setIsSearching(false);
    }
  };

  // 2. Lógica de Migração (Sem alterações)
  const handleMigrate = async () => {
    const toMigrate = candidates.filter((c) => selectedCandidates[c.id]);
    if (toMigrate.length === 0) return;

    setIsMigrating(true);
    let successCount = 0;

    for (const candidate of toMigrate) {
      const success = await addClient({
        name: candidate.name,
        phone: candidate.phone,
        city: candidate.city,
        source: `migration_${sourceType}`,
      });
      if (success) successCount++;
    }

    setIsMigrating(false);
    toast.success(
      `${successCount} clientes importados/atualizados com sucesso!`
    );
    setCandidates([]);
  };

  const toggleSelection = (id) => {
    setSelectedCandidates((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <div className="p-4 border rounded-lg bg-gray-50 dark:bg-card">
      <h3 className="text-lg font-bold mb-4">
        Ferramenta de Migração de Clientes
      </h3>

      <div className="flex flex-wrap gap-4 items-end mb-6">
        {/* Seletor de Origem */}
        <div className="w-[180px]">
          <Label>Origem dos Dados</Label>
          <Select value={sourceType} onValueChange={setSourceType}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="excursions">Excursões</SelectItem>
              <SelectItem value="reservations">Chalés (Reservas)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* ✅ Seletor de Tipo de Filtro */}
        <div className="w-[180px]">
          <Label>Filtrar Por</Label>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Mês</SelectItem>
              <SelectItem value="date">Data Exata</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* ✅ Input Dinâmico (Mês ou Data) */}
        <div className="w-[200px]">
          <Label>
            {filterType === "month" ? "Selecionar Mês" : "Selecionar Data"}
          </Label>
          {filterType === "month" ? (
            <Input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            />
          ) : (
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              // Adiciona classe para forçar o datepicker claro se necessário
              className="force-light-datepicker"
            />
          )}
        </div>

        <Button onClick={handleSearch} disabled={isSearching}>
          {isSearching ? "Buscando..." : "Buscar Registros"}
        </Button>
      </div>

      {candidates.length > 0 && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Selecione os clientes que deseja cadastrar na base oficial:
            </p>
            <Button onClick={handleMigrate} disabled={isMigrating}>
              {isMigrating
                ? "Processando..."
                : `Confirmar Importação (${
                    Object.values(selectedCandidates).filter(Boolean).length
                  })`}
            </Button>
          </div>

          <div className="border rounded-md bg-white dark:bg-background max-h-[500px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px] text-center">
                    Importar
                  </TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Telefone</TableHead>
                  {sourceType === "excursions" && <TableHead>Cidade</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {candidates.map((candidate) => (
                  <TableRow key={candidate.id}>
                    <TableCell className="text-center">
                      <Checkbox
                        checked={!!selectedCandidates[candidate.id]}
                        onCheckedChange={() => toggleSelection(candidate.id)}
                      />
                    </TableCell>
                    <TableCell>{candidate.name}</TableCell>
                    <TableCell>{candidate.phone}</TableCell>
                    {sourceType === "excursions" && (
                      <TableCell>{candidate.city}</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsMigrationTool;
